import { z } from 'zod';

/**
 * References to TimeLog-owned data (employees, roles, customers).
 *
 * The module never masters these — it stores the TimeLog identity plus a snapshot
 * of the fields it actually uses, captured at a known time. Calculations always
 * run on stored snapshots so a plan never silently reprices when TimeLog data
 * changes; refreshing a snapshot is an explicit edit (BR-13). Where TimeLog's
 * own value is a *schedule* (a time-phased cost rate), the schedule is what is
 * snapshotted and the calculation picks the period valid on the evaluation day —
 * a scheduled change is not a silent one.
 */

const externalRef = <T extends z.ZodType>(snapshot: T) =>
  z.object({
    /** Identity in the TimeLog world (TLP id, employee number, …). */
    tlpId: z.string().min(1),
    snapshot,
    capturedAt: z.string(),
  });

/**
 * A role is the *name of an hourly rate* (legacy TimeLog model), identified by
 * its hourly-rate **ServiceID** — the identity that survives a price change.
 * `tlpId` holds the ServiceID as a string. It is NOT the `HourlyRateID` (that
 * is one priced row and changes when the amount does) and NOT an RBAC RoleID
 * (`/api/v1/role` is the security catalogue and plays no part in pricing).
 *
 * The snapshot may carry the role's **cost price** per hour (ADR 0042) when
 * the source exposed one — today the prototype stand-in drawn from the
 * tenant's `/v1/cost-price` catalogue (`COST_PRICE_SOURCE=stand-in`). It is
 * what a role-only line costs at (CALC-21) and what Cost-To-Cost derives
 * revenue from (CALC-24). Absent = unknown; cost falls through to the contract
 * rule. Snapshotted at pick time like every other external value (BR-13).
 */
export const RoleRefSchema = externalRef(
  z.object({
    name: z.string().min(1),
    /** Cost per hour, currency/h. */
    costRate: z.number().min(0).optional(),
  }),
);
export type RoleRef = z.infer<typeof RoleRefSchema>;

/**
 * One validity period of a time-phased rate. `validFrom`/`validTo` are ISO
 * calendar days; `null` means open-ended on that side. The period containing
 * the evaluation day applies; before the first period the first period's rate
 * applies, and the last period is open-ended, so coverage is total (D4/D14).
 */
export const CostRatePeriodSchema = z.object({
  validFrom: z.string().nullable(),
  validTo: z.string().nullable(),
  /** Cost per hour, currency/h. */
  costRate: z.number().min(0),
});
export type CostRatePeriod = z.infer<typeof CostRatePeriodSchema>;

/**
 * A person snapshot carries the employee's time-phased cost-rate schedule when
 * the source exposed it (the fixture TimeLog does; live TimeLog does not yet).
 * Absent = unknown, and cost falls through to the role/contract rule (CALC-21).
 * It deliberately does NOT carry a role: the line's role is the line's role,
 * and the employee's default role is only a picker suggestion (D16).
 */
export const PersonRefSchema = externalRef(
  z.object({
    name: z.string().min(1),
    costRates: z.array(CostRatePeriodSchema).optional(),
  }),
);
export type PersonRef = z.infer<typeof PersonRefSchema>;

export const CustomerRefSchema = externalRef(z.object({ name: z.string().min(1) }));
export type CustomerRef = z.infer<typeof CustomerRefSchema>;

/** Convenience for fixtures and tests. */
export const roleRef = (
  tlpId: string,
  name: string,
  capturedAt = '2026-08-04T00:00:00Z',
  costRate?: number,
): RoleRef => ({
  tlpId,
  snapshot: { name, ...(costRate === undefined ? {} : { costRate }) },
  capturedAt,
});

export const personRef = (
  tlpId: string,
  name: string,
  capturedAt = '2026-08-04T00:00:00Z',
  costRates?: CostRatePeriod[],
): PersonRef => ({
  tlpId,
  snapshot: { name, ...(costRates === undefined ? {} : { costRates }) },
  capturedAt,
});

export const SupplierRefSchema = externalRef(
  z.object({ name: z.string().min(1), discipline: z.string().optional() }),
);
export type SupplierRef = z.infer<typeof SupplierRefSchema>;

export const supplierRef = (
  tlpId: string,
  name: string,
  discipline?: string,
  capturedAt = '2026-08-04T00:00:00Z',
): SupplierRef => ({
  tlpId,
  snapshot: { name, ...(discipline === undefined ? {} : { discipline }) },
  capturedAt,
});
